import { useEffect } from 'react';
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';

import { ComposerPage } from './pages/composer';
import { ConnectionsPage } from './pages/connections';
import { LoginPage } from './pages/login';
import { RunDetailPage } from './pages/run-detail';
import { RunsPage } from './pages/runs';
import { VideoComposerPage } from './pages/video-composer';
import { VideoRunDetailPage } from './pages/video-run-detail';
import { useAuthStore } from './stores/auth-store';

function Protected({ children }: { children: React.ReactNode }) {
  const session = useAuthStore((s) => s.session);
  const loading = useAuthStore((s) => s.loading);
  if (loading) return <div className="muted">Loading…</div>;
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function Header() {
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const location = useLocation();
  if (!user) return null;
  const isActive = (path: string) =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(path);
  return (
    <header className="topbar">
      <div className="topbar-left">
        <img src="/logo-mark.svg" alt="" className="topbar-logo" />
        <span className="topbar-brand">NewsWave</span>
        <span className="topbar-sub">Social Composer</span>
        <nav className="topbar-nav">
          <Link
            to="/"
            className={`nav-link ${isActive('/') ? 'nav-link-active' : ''}`}
          >
            Carousels
          </Link>
          <Link
            to="/video"
            className={`nav-link ${isActive('/video') ? 'nav-link-active' : ''}`}
          >
            Videos
          </Link>
          <Link
            to="/connections"
            className={`nav-link ${isActive('/connections') ? 'nav-link-active' : ''}`}
          >
            Connections
          </Link>
        </nav>
      </div>
      <div className="topbar-right">
        <span className="dim small">{user.email}</span>
        <button className="btn-link" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </header>
  );
}

export function App() {
  const init = useAuthStore((s) => s.initFromSession);
  useEffect(() => {
    void init();
  }, [init]);

  return (
    <BrowserRouter>
      <Header />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <Protected>
              <ComposerPage />
            </Protected>
          }
        />
        <Route
          path="/runs"
          element={
            <Protected>
              <RunsPage />
            </Protected>
          }
        />
        <Route
          path="/runs/:id"
          element={
            <Protected>
              <RunDetailPage />
            </Protected>
          }
        />
        <Route
          path="/video"
          element={
            <Protected>
              <VideoComposerPage />
            </Protected>
          }
        />
        <Route
          path="/video/runs"
          element={<Navigate to="/video" replace />}
        />
        <Route
          path="/video/runs/:id"
          element={
            <Protected>
              <VideoRunDetailPage />
            </Protected>
          }
        />
        <Route
          path="/connections"
          element={
            <Protected>
              <ConnectionsPage />
            </Protected>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
